// Recording what we knew at the moment an error was shown.
//
// Kevin, 2026-08-20: "can you also create a way to store any information needed when an
// error pops up."
//
// Every error this session cost a round trip of guesswork, because the evidence was gone by
// the time anyone asked: an empty-bodied 500 that could have been a timeout or a crash; a
// reason swallowed by a `.catch()` behind "NetSuite may be busy"; a 429 with no record of how
// long it had retried. This captures the answer at the moment it is still knowable.
//
// TWO RULES, both load-bearing:
//
//   1. RECORDING AN ERROR MUST NEVER CAUSE ONE. Nothing here throws and nothing here is
//      awaited on the user's path. A save that has already gone wrong must not then produce
//      a second dialog because the logging failed.
//   2. NO SECRETS. Only what the browser already holds. The endpoint's columns are fixed, so
//      an unexpected field is dropped rather than stored.
//
// The two fields that matter most are `fingerprint` and `configSnapshot`: together they let
// the exact configuration be reloaded and the failure re-run, which is the difference
// between diagnosing a bug and waiting for it to happen again.

import { API_BASE } from "./netsuiteClient";
import { getLaunchContext } from "./launchContext";

export type ErrorReport = {
  /** Exactly what the user was shown. The only required field. */
  message: string;
  /** Where in the submit flow it happened, e.g. "create the item (attempt 2)". */
  phase?: string;
  /** The endpoint's own failure step, when it named one: bomRevision, verifyItem, ... */
  step?: string;
  detail?: string;
  httpStatus?: number;
  rejected?: boolean;
  opaque?: boolean;
  retryable?: boolean;
  product?: string;
  configMode?: string;
  itemId?: string | number | null;
  itemName?: string | null;
  fingerprint?: string | null;
  /** The compressed answer state, as sent to create-assembly. */
  configSnapshot?: string | null;
  /** Per-call NetSuite timing from the failed request, when the response carried it. */
  timing?: unknown;
  /** Anything else worth keeping — attempt counts, which retry path ran, and so on. */
  context?: Record<string, unknown>;
};

/** The built asset id, so a stale tab running old code is identifiable rather than a mystery. */
function appBuild(): string {
  try {
    const el = Array.from(document.querySelectorAll("script[src]")).find((s) =>
      /assets\/index-.*\.js/.test((s as HTMLScriptElement).src)
    ) as HTMLScriptElement | undefined;
    const m = el ? el.src.match(/assets\/(index-[^/]+\.js)/) : null;
    return m ? m[1] : "unknown";
  } catch {
    return "unknown";
  }
}

/** Pull the useful parts off a thrown value, so callers do not have to remember to. */
export function describeThrown(e: unknown): Pick<ErrorReport, "detail" | "httpStatus" | "rejected" | "opaque" | "retryable" | "step"> {
  const get = <T,>(k: string): T | undefined =>
    e && typeof e === "object" && k in e ? (e as Record<string, T>)[k] : undefined;
  return {
    detail: e instanceof Error ? e.message : String(e),
    httpStatus: get<number>("httpStatus"),
    rejected: get<boolean>("rejected"),
    opaque: get<boolean>("opaque"),
    retryable: get<boolean>("retryable"),
    step: get<string>("step"),
  };
}

/**
 * Record an error. FIRE AND FORGET — never awaited, never throws, never rejects.
 *
 * `keepalive` so the record still leaves the browser if the tab is closing, which is exactly
 * when a frustrated person closes it.
 */
export function recordError(report: ErrorReport): void {
  try {
    if (!report || !String(report.message || "").trim()) return;
    const ctx = getLaunchContext();
    const body = JSON.stringify({
      ...report,
      itemId: report.itemId == null ? null : String(report.itemId),
      userId: ctx.userId ?? null,
      userName: ctx.userName ?? null,
      appBuild: appBuild(),
      userAgent: typeof navigator === "undefined" ? null : navigator.userAgent,
    });
    void fetch(`${API_BASE}/error-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      /* rule 1: a logging failure is not the user's problem */
    });
  } catch {
    /* rule 1 again: nothing about reporting may propagate */
  }
}
